import { Button, cn } from "@breenova/ui";
import { Maximize2, Minus, Plus } from "lucide-react";
import { useCallback, useRef, useState, type PointerEvent, type ReactNode, type WheelEvent } from "react";

const MIN_SCALE = 0.4;
const MAX_SCALE = 2;
const STEP = 0.2;

function clamp(scale: number): number {
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));
}

export interface ZoomableCanvasProps {
  children: ReactNode;
  /** Announced on the zoom controls, e.g. "pedigree". */
  label: string;
  /**
   * Drawn straight onto the page: no recessed panel behind it, and the zoom buttons only
   * in view where there is a mouse to use them. Pinch still zooms on a phone.
   */
  bare?: boolean;
  className?: string;
}

/**
 * A pan-and-zoom viewport for something wider than the screen.
 *
 * Three ways in, because they suit different hands: pinch with two fingers on a phone,
 * ctrl-scroll on a trackpad, and the buttons for everyone else. The buttons are not a
 * fallback — they are the only route that works with a keyboard or a screen reader, so
 * they are real controls rather than decoration.
 *
 * Panning is left to native overflow scrolling rather than reimplemented with pointer
 * maths: the browser already does momentum, scrollbars and two-finger trackpad panning
 * properly, and a custom version of that is invariably worse on some device.
 */
export function ZoomableCanvas({ children, label, bare = false, className }: ZoomableCanvasProps) {
  const [scale, setScale] = useState(1);

  // Live pointers, so a second finger turns the gesture into a pinch.
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const pinchStart = useRef<{ distance: number; scale: number } | null>(null);

  const zoomBy = useCallback((delta: number) => {
    setScale((current) => clamp(current + delta));
  }, []);

  function handleWheel(event: WheelEvent<HTMLDivElement>): void {
    // A trackpad pinch arrives as a wheel event with ctrlKey set. Without ctrlKey it is
    // an ordinary scroll and must be left alone to pan the container.
    if (!event.ctrlKey) {
      return;
    }

    event.preventDefault();
    zoomBy(event.deltaY > 0 ? -STEP / 2 : STEP / 2);
  }

  function handlePointerDown(event: PointerEvent<HTMLDivElement>): void {
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
  }

  function handlePointerMove(event: PointerEvent<HTMLDivElement>): void {
    if (!pointers.current.has(event.pointerId)) {
      return;
    }

    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });

    const [first, second] = [...pointers.current.values()];
    if (!first || !second) {
      // One finger: let the browser scroll the container.
      pinchStart.current = null;
      return;
    }

    const distance = Math.hypot(second.x - first.x, second.y - first.y);

    if (!pinchStart.current) {
      pinchStart.current = { distance, scale };
      return;
    }

    // Scale relative to where the pinch began, so the gesture feels attached to the
    // fingers rather than accumulating drift over a long pinch.
    setScale(clamp(pinchStart.current.scale * (distance / pinchStart.current.distance)));
  }

  function endPointer(event: PointerEvent<HTMLDivElement>): void {
    pointers.current.delete(event.pointerId);

    if (pointers.current.size < 2) {
      pinchStart.current = null;
    }
  }

  return (
    <div className={cn("relative", className)}>
      <div
        className={cn(
          "absolute right-sm top-sm z-10 items-center gap-xs",
          // Out of sight on a phone but still reachable: the buttons are the one route that
          // works with a screen reader.
          bare ? "sr-only flex md:not-sr-only" : "flex",
        )}
      >
        <Button
          variant="secondary"
          size="icon"
          aria-label={`Zoom out of the ${label}`}
          disabled={scale <= MIN_SCALE}
          onClick={() => zoomBy(-STEP)}
        >
          <Minus className="size-4" />
        </Button>
        <Button
          variant="secondary"
          size="icon"
          aria-label={`Zoom in on the ${label}`}
          disabled={scale >= MAX_SCALE}
          onClick={() => zoomBy(STEP)}
        >
          <Plus className="size-4" />
        </Button>
        <Button
          variant="secondary"
          size="icon"
          aria-label={`Reset the ${label} zoom`}
          onClick={() => setScale(1)}
        >
          <Maximize2 className="size-4" />
        </Button>
      </div>

      <div
        className={cn(
          "h-full overflow-auto",
          bare ? "" : "rounded-card bg-surface-container p-lg",
          // Lets a one-finger drag pan while a two-finger gesture reaches our handler.
          "touch-pan-x touch-pan-y",
        )}
        onWheel={handleWheel}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endPointer}
        onPointerCancel={endPointer}
        onPointerLeave={endPointer}
      >
        <div
          // Width grows with the content, so scaling up genuinely extends the scrollable
          // area instead of clipping the tree at the original box.
          style={{ transform: `scale(${scale})`, transformOrigin: "left top" }}
          className="inline-block origin-top-left transition-transform duration-100"
        >
          {children}
        </div>
      </div>

      <p className="sr-only" aria-live="polite">
        Zoom {Math.round(scale * 100)} percent
      </p>
    </div>
  );
}
